// Ledige tider og forslag til steamer (Smartplan-logikken).
//
// Regler:
//  - En steamer kan kun få en opgave på en dag, hvor han arbejder og ikke har fravær, og som ikke er lukkedag.
//  - En særlig dag (fx længere dag eller fri) går forud for den faste ugeplan.
//  - Mellem to opgaver skal der være mindst `pause` minutter, og mindst den estimerede køretid.
//  - Opgaven skal være færdig inden arbejdstidens slut.
//  - Steamere starter hjemmefra (start_postnr). Den første opgave må starte ved arbejdstidens start.
//  - En steamer med zoner kører kun i dem. En steamer med en liste af ydelser laver kun dem.
//  - Forslag sorteres efter mindst ekstra kørsel, så den nærmeste steamer kommer først.
//  - Reklamationer: kun den steamer, der lavede den oprindelige opgave (kunSteamer).
//  - Steamerens faste fri (fastFri.ts) tages aldrig – kun kontoret kan lægge en opgave dér, manuelt.
import { friPaa } from './fastFri.ts';
import { ugedagMandag1 } from './dato.ts';
import { postnrPunkt, koeretidMin, iZone, type Punkt } from './geo.ts';
import type { Arbejdstid, Fravaer, SaerligDag, Steamer, Zone } from './typer.ts';

/**
 * pause: mindst så mange minutter mellem to opgaver (eller køretiden, hvis den er længere).
 * trin: starttider hvert X minut (fleksibel model).
 * faste: faste starttider pr. dag i stedet for trin (fx 08:00, 11:00, 14:00).
 * bufferFoer/bufferEfter: ekstra minutter før og efter hver opgave (klargøring, oprydning).
 * maxOpgaver: højst så mange opgaver pr. steamer pr. dag (kan også sættes pr. steamer).
 */
export type Regler = { pause: number; trin: number; maxKoersel: number; faste?: number[] | null; bufferFoer?: number; bufferEfter?: number; maxOpgaver?: number | null };
export const STANDARD_REGLER: Regler = { pause: 30, trin: 30, maxKoersel: 90, faste: null, bufferFoer: 0, bufferEfter: 0, maxOpgaver: null };

/** bF/bE: opgavens egen buffer før/efter (sat på bookingen); ellers reglernes. */
export type DagsOpgave = { id?: number; steamer_id: string; start: number; slut: number; postnr: string; bF?: number | null; bE?: number | null };
export type Kandidat = { steamer_id: string; start: number; slut: number; ekstraKoersel: number; koerselFoer: number };

export function harFravaer(fravaer: Fravaer[], steamer_id: string, dato: string): Fravaer | undefined {
  return fravaer.find((f) => f.steamer_id === steamer_id && f.fra <= dato && f.til >= dato);
}

export function koersel(a: Punkt | null, b: Punkt | null): number {
  if (!a || !b) return 60; // ukendt sted: regn forsigtigt
  return koeretidMin(a, b);
}

/** Steamerens arbejdstid på en dato, eller null hvis han ikke arbejder. */
export function arbejdstidPaa(a: {
  steamer: Steamer; dato: string; arbejdstider: Arbejdstid[]; fravaer: Fravaer[];
  saerligeDage?: SaerligDag[]; lukkedage?: string[];
}): { start: number; slut: number } | null {
  if (!a.steamer.aktiv) return null;
  if (a.lukkedage?.includes(a.dato)) return null;
  if (harFravaer(a.fravaer, a.steamer.id, a.dato)) return null;
  const s = a.saerligeDage?.find((x) => x.steamer_id === a.steamer.id && x.dato === a.dato);
  if (s) return s.slut > s.start ? { start: s.start, slut: s.slut } : null;
  const at = a.arbejdstider.find((x) => x.steamer_id === a.steamer.id && x.ugedag === ugedagMandag1(a.dato));
  return at ? { start: at.start, slut: at.slut } : null;
}

export type KandidatArgs = {
  steamer: Steamer; dato: string; arbejdstider: Arbejdstid[]; fravaer: Fravaer[];
  opgaver: DagsOpgave[]; postnr: string; varighed: number; tidligst?: number; ignorerId?: number;
  kunStart?: number; saerligeDage?: SaerligDag[]; lukkedage?: string[];
  zoner?: Zone[]; ydelse_id?: string; regler?: Regler;
};

/** Må steameren køre til postnummeret og lave ydelsen? */
export function steamerPasser(s: Steamer, postnr: string, zoner: Zone[] = [], ydelse_id?: string): boolean {
  if (ydelse_id && s.ydelser?.length && !s.ydelser.includes(ydelse_id)) return false;
  if (s.zoner?.length) {
    const mine = zoner.filter((z) => s.zoner!.includes(z.id));
    if (mine.length && !mine.some((z) => iZone(postnr, z))) return false;
  }
  return true;
}

/** Alle mulige starttider for én steamer på én dag for en ny opgave. */
export function kandidaterForSteamer(args: KandidatArgs): Kandidat[] {
  const { steamer, postnr, varighed } = args;
  const r = args.regler ?? STANDARD_REGLER;
  if (!steamerPasser(steamer, postnr, args.zoner, args.ydelse_id)) return [];
  const at = arbejdstidPaa(args);
  if (!at) return [];
  const ny = postnrPunkt(postnr);
  const hjem = postnrPunkt(steamer.start_postnr);
  const dag = args.opgaver
    .filter((o) => o.steamer_id === steamer.id && (args.ignorerId == null || o.id !== args.ignorerId))
    .sort((a, b) => a.start - b.start);
  const ud: Kandidat[] = [];
  const max = steamer.maxOpgaver ?? r.maxOpgaver;
  if (max != null && max > 0 && dag.length >= max) return [];
  const bF = r.bufferFoer ?? 0, bE = r.bufferEfter ?? 0;
  const fri = friPaa(steamer, args.dato, args.saerligeDage);
  const foerst = Math.max(at.start + bF, args.tidligst ?? 0);
  const tider: number[] = [];
  if (args.kunStart != null) {
    if (args.kunStart >= foerst) tider.push(args.kunStart);
  } else if (r.faste?.length) {
    for (const t of [...r.faste].sort((a, b) => a - b)) if (t >= foerst) tider.push(t);
  } else {
    for (let t = Math.ceil(foerst / r.trin) * r.trin; t + varighed + bE <= at.slut; t += r.trin) tider.push(t);
  }
  // Mellem to opgaver: bufferen efter den første + pause/køretid + bufferen før den næste.
  for (const t of tider) {
    const slut = t + varighed;
    if (slut + bE > at.slut) continue;
    if (dag.some((o) => o.start - (o.bF ?? bF) < slut + bE && o.slut + (o.bE ?? bE) > t - bF)) continue;
    if (fri.some((f) => f.fra < slut && f.til > t)) continue;
    const foer = [...dag].reverse().find((o) => o.slut <= t) ?? null;
    const efter = dag.find((o) => o.start >= slut) ?? null;
    const pFoer = foer ? postnrPunkt(foer.postnr) : hjem;
    const kFoer = koersel(pFoer, ny);
    if (foer && t < foer.slut + (foer.bE ?? bE) + Math.max(r.pause, kFoer) + bF) continue;
    let kEfter = 0, kDirekte = 0;
    if (efter) {
      const pEfter = postnrPunkt(efter.postnr);
      kEfter = koersel(ny, pEfter);
      if (slut + bE + Math.max(r.pause, kEfter) + (efter.bF ?? bF) > efter.start) continue;
      kDirekte = koersel(pFoer, pEfter);
    }
    const ekstra = Math.max(0, kFoer + kEfter - kDirekte);
    ud.push({ steamer_id: steamer.id, start: t, slut, ekstraKoersel: ekstra, koerselFoer: kFoer });
  }
  return ud;
}

export type ForslagArgs = Omit<KandidatArgs, 'steamer'> & { steamere: Steamer[]; kunSteamer?: string | null };

/** Bedste forslag på tværs af steamere, sorteret efter mindst ekstra kørsel og derefter tid. */
export function forslag(args: ForslagArgs): Kandidat[] {
  const liste = args.steamere.filter((s) => !args.kunSteamer || s.id === args.kunSteamer);
  return liste
    .flatMap((steamer) => kandidaterForSteamer({ ...args, steamer }))
    .sort((a, b) => a.ekstraKoersel - b.ekstraKoersel || a.start - b.start);
}

/**
 * Tider kunden kan vælge på hjemmesiden: én pr. starttid, med den steamer der kører mindst.
 * Tider med for lang kørsel vises ikke (de kan stadig bookes af kontoret).
 */
export function ledigeTider(args: ForslagArgs): { start: number; slut: number; steamer_id: string }[] {
  const max = (args.regler ?? STANDARD_REGLER).maxKoersel;
  const pr = new Map<number, Kandidat>();
  for (const k of forslag(args)) {
    if (k.koerselFoer > max) continue;
    const nu = pr.get(k.start);
    if (!nu || k.ekstraKoersel < nu.ekstraKoersel) pr.set(k.start, k);
  }
  return [...pr.values()].sort((a, b) => a.start - b.start).map(({ start, slut, steamer_id }) => ({ start, slut, steamer_id }));
}

